import { Link, useNavigate } from 'react-router-dom'
import { Correct } from '../../components/Correct'
import { dayLabel, weekLabel } from '../../lib/weekly'
import type { WeekAhead as WeekAheadView } from '../../types/weekly'

// The week ahead: the meetings on the calendar with a prep brief a click away, the next steps due (what you owe
// first), the tactics the methodology says come next, and the quiet ties worth a touch.
const fmtTime = (iso: string) => new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })

export function WeekAhead({ ahead, calendarConnected }: { ahead: WeekAheadView; calendarConnected: boolean }) {
  const navigate = useNavigate()
  return (
    <div className="card ahead-card">
      <div className="card-head">
        <div>
          <h2>Week ahead</h2>
          <p className="muted">{weekLabel(ahead.window)}: what is on the calendar, what is due, which plays come next, who to touch.</p>
        </div>
      </div>
      <div className="ahead-grid">
        <section className="ahead-block">
          <h3>Meetings scheduled <span className="muted small">{ahead.meetings.length}</span></h3>
          {!calendarConnected && <p className="muted small">No calendar connected. <Link to="/meetings">Connect it</Link> and next week's client meetings land here with a prep brief each.</p>}
          {calendarConnected && ahead.meetings.length === 0 && <p className="muted small">No external meetings on the calendar for next week.</p>}
          <ul className="plain ahead-list">
            {ahead.meetings.map((m) => (
              <li key={m.meetingId} className="ahead-meeting">
                <span className="ahead-when"><strong>{dayLabel(m.start.slice(0, 10))}</strong><span className="muted small">{fmtTime(m.start)}</span></span>
                <span className="ahead-body">
                  <strong>{m.subject}</strong>
                  <span className="muted small">{[m.companyName, m.contacts.slice(0, 3).join(', ')].filter(Boolean).join(' · ')}</span>
                </span>
                {m.companyId && <button className="button small primary" onClick={() => navigate(`/accounts/${encodeURIComponent(m.companyId!)}`)}>Prep</button>}
              </li>
            ))}
          </ul>
        </section>

        <section className="ahead-block">
          <h3>Next steps due <span className="muted small">{ahead.steps.length}</span></h3>
          {ahead.steps.length === 0 && <p className="muted small">No next step on file falls due next week.</p>}
          <ul className="plain ahead-list">
            {ahead.steps.map((s) => (
              <li key={s.engagementId} className={`ahead-step ${s.overdue ? 'overdue' : ''}`}>
                <span className="ahead-body">
                  <strong>{s.nextStep}</strong>
                  <span className="muted small">{s.subject} · {s.companyName}{s.by ? ` · by ${dayLabel(s.by)}` : ''}</span>
                </span>
                <span className="ahead-side">
                  <span className={`tag ${s.mine ? 'owed' : 'awaited'}`}>{s.mine ? 'you owe' : 'they owe'}</span>
                  {s.overdue && <span className="tag sig high">overdue</span>}
                  <Correct kind="next-step" entityId={s.engagementId} entityLabel={s.nextStep} currentValue={s.mine ? 'mine' : 'theirs'} compact />
                </span>
              </li>
            ))}
          </ul>
        </section>

        <section className="ahead-block">
          <h3>Tactics due <span className="muted small">{ahead.tactics.length}</span></h3>
          {ahead.tactics.length === 0 && <p className="muted small">Nothing planned and nothing suggested by the methodology. <Link to="/playbook">Open the playbook</Link>.</p>}
          <ul className="plain ahead-list">
            {ahead.tactics.map((t) => (
              <li key={`${t.tacticId}-${t.engagementId}`} className="ahead-tactic">
                <span className="ahead-body">
                  <strong><Link to="/playbook">{t.tacticName}</Link></strong>
                  <span className="muted small">{t.subject} · {t.companyName} · {t.why}</span>
                </span>
                <span className={`tag ${t.planned ? 'planned' : 'suggest'}`}>{t.planned ? 'planned' : 'suggested'}</span>
                {t.correctionId && (
                  <Correct kind="play" entityId={t.correctionId} entityLabel={t.tacticName} currentValue={`tactic:${t.tacticId}`} compact />
                )}
              </li>
            ))}
          </ul>
        </section>

        <section className="ahead-block">
          <h3>Quiet ties worth a touch <span className="muted small">{ahead.contacts.length}</span></h3>
          {ahead.contacts.length === 0 && <p className="muted small">No strong tie has gone quiet.</p>}
          <ul className="plain ahead-list">
            {ahead.contacts.map((c) => (
              <li key={c.personId} className="ahead-contact">
                <span className="ahead-body">
                  <strong><Link to="/contacts">{c.name}</Link></strong>
                  <span className="muted small">{[c.title, c.companyName].filter(Boolean).join(', ')}{c.lastSubject ? ` · last on "${c.lastSubject}"` : ''}</span>
                </span>
                <span className="ahead-side">
                  <span className="strength-bar" title={`strength ${Math.round(c.strength * 100)}%`}><span className={`strength-fill ${c.strength >= 0.5 ? 'strong' : 'warm'}`} style={{ width: `${Math.round(c.strength * 100)}%` }} /></span>
                  <span className="muted small">{c.dormancyDays} days</span>
                </span>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  )
}
